/**
 * TAILWIND SELECT KOMPONENTE
 *
 * Dropdown basierend auf HeadlessUI Listbox mit Floating-UI Positionierung.
 * Wird nie von Parent-Containern abgeschnitten (Portal-Rendering).
 *
 * Props:
 *   value       - string, aktuell ausgewählter Wert
 *   onChange     - (value: string) => void
 *   options      - Array von { value: string, label: string, icon?: ReactNode }
 *   placeholder? - string (default: "Auswählen...")
 *   className?   - zusätzliche Klassen für den Container (z.B. "w-[160px]")
 *   disabled?    - boolean
 *
 * Usage:
 *   <TWSelect
 *     value={selected}
 *     onChange={setSelected}
 *     options={[
 *       { value: 'all', label: 'Alle' },
 *       { value: 'de', label: 'Deutschland', icon: <Flag /> },
 *     ]}
 *     className="w-[160px]"
 *   />
 */

import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import { ChevronDown, Check } from 'lucide-react';
import clsx from 'clsx';

export interface TWSelectOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
}

interface TWSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: TWSelectOption[];
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export const TWSelect: React.FC<TWSelectProps> = ({
  value,
  onChange,
  options,
  placeholder = 'Auswählen...',
  className,
  disabled = false,
}) => {
  const selectedOption = options.find(opt => opt.value === value);

  return (
    <Listbox value={value} onChange={onChange} disabled={disabled}>
      <div className={clsx('relative', className)}>
        <ListboxButton
          className={clsx(
            'relative w-full cursor-pointer rounded-lg py-1.5 pl-3 pr-8 text-left text-base sm:text-sm',
            'bg-gray-100 dark:bg-white/5',
            'text-gray-900 dark:text-white',
            'outline-1 -outline-offset-1',
            'outline-gray-300 dark:outline-white/10',
            'focus:outline-2 focus:-outline-offset-2 focus:outline-brand',
            'transition-colors',
            'disabled:opacity-50 disabled:cursor-not-allowed'
          )}
        >
          <span className="flex items-center gap-1.5 truncate">
            {selectedOption?.icon && (
              <span className="shrink-0">{selectedOption.icon}</span>
            )}
            {selectedOption?.label || placeholder}
          </span>
          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
            <ChevronDown className="h-4 w-4 text-gray-400 dark:text-gray-500" />
          </span>
        </ListboxButton>

        <ListboxOptions
          anchor="bottom start"
          transition
          className={clsx(
            'z-[10010] mt-1 max-h-60 min-w-[var(--button-width)] overflow-auto rounded-lg py-1',
            'bg-white dark:bg-gray-900',
            'outline-1 -outline-offset-1 outline-gray-300 dark:outline-white/10',
            'shadow-lg dark:shadow-gray-900/50',
            'focus:outline-none',
            'transition data-[closed]:opacity-0 data-[closed]:scale-95',
            'data-[enter]:duration-100 data-[enter]:ease-out',
            'data-[leave]:duration-75 data-[leave]:ease-in'
          )}
        >
          {options.map((option) => (
            <ListboxOption
              key={option.value}
              value={option.value}
              className={clsx(
                'relative cursor-pointer select-none py-1.5 pl-3 pr-8 text-sm',
                'text-gray-700 dark:text-gray-300',
                'data-[focus]:bg-gray-100 dark:data-[focus]:bg-gray-700',
                'data-[selected]:font-medium',
                'data-[selected]:text-gray-900 dark:data-[selected]:text-white'
              )}
            >
              {({ selected }) => (
                <>
                  <span className={clsx('flex items-center gap-1.5 truncate', selected && 'font-medium')}>
                    {option.icon && (
                      <span className="shrink-0">{option.icon}</span>
                    )}
                    {option.label}
                  </span>
                  {selected && (
                    <span className="absolute inset-y-0 right-0 flex items-center pr-2">
                      <Check className="h-4 w-4 text-brand" />
                    </span>
                  )}
                </>
              )}
            </ListboxOption>
          ))}
        </ListboxOptions>
      </div>
    </Listbox>
  );
};
